import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import useBiogenLincConfig from './BiogenLincConfigGroup.hook';
import CardForm from 'components/CardForm';
import CardSwitch from 'components/CardSwitch';
import { Market } from 'generated/event.types';
import { EventPromotionalityEnum } from 'modules/events/basics/enums/events.enums';
import InformativeTooltip from 'modules/events/components/InformativeTooltip';
import PrcBlock from 'modules/events/components/PrcBlock';
import useCommunicationStep from 'modules/events/routes/CreateOnlineMeeting/Steps/CommunicationStep/CommunicationStep.hook';
import Box from 'yoda-ui/components/Box';
import CheckBoxYoda from 'yoda-ui/components/Form/CheckBoxYoda';
import RadioGroup from 'yoda-ui/components/Form/RadioGroup';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const BiogenLincConfigGroup: FC = () => {
  const { isVisibleOnBiogenLinc, isPublic, isGuestRegistrationEnabled } = useCommunicationStep();
  const {
    isVisibleOnBiogenLincConfig,
    eventPromotionalityConfig,
    prcValidationsConfig,
    isSwitzerlandSpecific,
    eventMarket,
    isCollectTextMessageUserConsentEnabled,
    t,
  } = useBiogenLincConfig();

  const guestRegistrationCountryDropdownMarkets = [Market.com, Market.gulf, Market.nordics, Market.ar];

  const flags = useFlags();

  return (
    <Box>
      <Box paddingBottom={ YodaSpacing.small }>
        <CardSwitch { ...isVisibleOnBiogenLincConfig } />
      </Box>
      {
        isVisibleOnBiogenLinc && (
          <>
            <Box paddingBottom={ YodaSpacing.small }>
              <CardForm title={ eventPromotionalityConfig.formGroupConfig.title }>
                <RadioGroup { ...eventPromotionalityConfig.radioGroupConfig } />
                {
                  isPublic === EventPromotionalityEnum.public
                 && (
                   <Box display="flex" alignItems="center">
                     <CheckBoxYoda { ...eventPromotionalityConfig.enableGuestRegistration } />
                     <InformativeTooltip
                       title={ t('events_enable_guest_registration_description') }
                     />
                   </Box>
                 )
                }

                {
                  isGuestRegistrationEnabled && (
                    <Box display="flex" flexDirection="column">
                      {
                        guestRegistrationCountryDropdownMarkets.includes(eventMarket as Market)
                          && <Box display="flex" alignItems="center">
                            <CheckBoxYoda { ...eventPromotionalityConfig.enableGuestRegistrationCountryDropdown } />
                            <InformativeTooltip
                              title={ t('events_enable_guest_registration_country_dropdown_description') }
                            />
                          </Box>
                      }
                      <Box display="flex" alignItems="center">
                        <CheckBoxYoda { ...eventPromotionalityConfig.checkConsent } />
                        <InformativeTooltip
                          title={ t('events_enable_guest_check_consent_description') }
                        />
                      </Box>
                      {
                        isCollectTextMessageUserConsentEnabled
                        && <Box display="flex" alignItems="center">
                          <CheckBoxYoda { ...eventPromotionalityConfig.checkGuestTextMessageConsent } />
                          <InformativeTooltip
                            title={ t('events_enable_guest_check_text_message_consent_description') }
                          />
                        </Box>
                      }
                      <Box display="flex" alignItems="center">
                        <CheckBoxYoda { ...eventPromotionalityConfig.collectPPAndToUUserConsents } />
                        <InformativeTooltip
                          title={ t('events_enable_guest_pp_and_tou_consents_description') }
                        />
                      </Box>
                      <Box display="flex" alignItems="center">
                        <CheckBoxYoda { ...eventPromotionalityConfig.showEmailCheckModal } />
                        <InformativeTooltip
                          title={ t('show_email_check_modal_description') }
                        />
                      </Box>
                      {
                        flags.cweb88EnablePublicForm
                      && <Box display="flex" alignItems="center">
                        <CheckBoxYoda { ...eventPromotionalityConfig.fullForm } />
                        <InformativeTooltip
                          title={ t('events_enable_guest_full_form_description') }
                        />
                      </Box>
                      }
                      {
                        flags.eventsCweb1249EnableShouldVeevaNetworkMasterSelection
                        && <Box display="flex" alignItems="center">
                          <CheckBoxYoda { ...eventPromotionalityConfig.shouldVeevaNetworkMaster } />
                          <InformativeTooltip
                            title={ t('events_enable_should_veeva_network_master_description') }
                          />
                        </Box>
                      }
                    </Box>
                  )
                }

                {
                  isPublic === EventPromotionalityEnum.gated && eventPromotionalityConfig.enableDeclineModal && (
                    <Box display="flex" flexDirection="column">
                      <CheckBoxYoda { ...eventPromotionalityConfig.enableDeclineModal } />
                    </Box>
                  )
                }
              </CardForm>
            </Box>
          </>
        )
      }
      <Box paddingBottom={ YodaSpacing.small }>
        <CardForm title={ t('prcValidation_title') }>
          <Box marginBottom={ YodaSpacing.large }>
            <PrcBlock { ...prcValidationsConfig.eventDetails } />
          </Box>
        </CardForm>
      </Box>

      {
        eventMarket === Market.com && flags.eventsBiogenlinc5399SwissMarketIntoTheBo && (
          <Box paddingBottom={ YodaSpacing.small }>
            <CardSwitch { ...isSwitzerlandSpecific } />
          </Box>
        )
      }

    </Box>
  );
};

export default BiogenLincConfigGroup;
